/**
 * ReminderFilters — modern filter pill bar for reminders list.
 */

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';

type Filter = 'active' | 'today' | 'week' | 'history';

interface Props {
  selected: Filter;
  onSelect: (filter: Filter) => void;
  counts?: { active: number; today: number; week: number; history: number };
}

export default function ReminderFilters({ selected, onSelect, counts }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const filterMeta: { key: Filter; label: string }[] = [
    { key: 'active', label: t('reminders.filterActive') },
    { key: 'today', label: t('reminders.filterToday') },
    { key: 'week', label: t('reminders.filterWeek') },
    { key: 'history', label: t('reminders.filterHistory') },
  ];

  return (
    <View style={styles.container}>
      {filterMeta.map(({ key, label }) => {
        const isActive = selected === key;
        const count = counts?.[key];
        return (
          <TouchableOpacity
            key={key}
            style={[styles.chip, isActive && styles.chipActive]}
            onPress={() => onSelect(key)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={label}
          >
            <Text style={[styles.chipText, isActive && styles.chipTextActive]} numberOfLines={1}>
              {label}
            </Text>
            {count !== undefined && (
              <View style={[styles.countBadge, isActive && styles.countBadgeActive]}>
                <Text style={[styles.countText, isActive && styles.countTextActive]}>
                  {count}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.sm,
  },
  chip: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.sm,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.surface,
    borderWidth: 0.5,
    borderColor: colors.border,
    gap: 4,
  },
  chipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  chipText: {
    flexShrink: 1,
    fontSize: FONT_SIZE.xs,
    fontWeight: '500',
    color: colors.textSecondary,
    textAlign: 'center',
  },
  chipTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  countBadge: {
    backgroundColor: colors.primaryLight,
    borderRadius: RADIUS.md,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  countBadgeActive: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  countText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  countTextActive: {
    color: '#fff',
  },
});
